// Why one connection per app: one EventSource per feature would multiply concurrent
// connections and Redis pub/sub subscriptions per user. Auth goes via cookie
// (withCredentials: true) since native EventSource doesn't support custom headers.

import { useEffect, useRef } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import { WEB_BASE_URL } from "@/utils/apiBaseUrls";

// /events/stream is served by apps/web, not by the lesson runtime.
const BASE_URL = WEB_BASE_URL;
const BACKOFF_BASE_MS = 500;
const BACKOFF_MAX_MS = 30_000; // cap at 30 s

export type SseHandlers = Record<string, (data: unknown) => void>;

export type SseClientOptions = {
  /** Called every time the connection opens — the first connect and every
   *  reconnect after a drop. Callers that must not miss events use it to
   *  re-read their state: SSE has no replay, so anything published while the
   *  connection was down is gone. Fires on the first connect too, so a caller
   *  that already loads on mount should skip that one. */
  onOpen?: () => void;
};

export function useAppSseClient(
  enabled: boolean,
  handlers: SseHandlers,
  options?: SseClientOptions,
): void {
  // Keep the latest handlers accessible inside the stable effect closure without
  // reopening the connection when the caller re-renders with new handler references.
  const handlersRef = useRef<SseHandlers>(handlers);
  handlersRef.current = handlers;

  // Same treatment, for the same reason: a caller passing a fresh object literal
  // each render must not tear down the connection.
  const optionsRef = useRef<SseClientOptions | undefined>(options);
  optionsRef.current = options;

  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryDelayRef = useRef<number>(BACKOFF_BASE_MS);
  const esRef = useRef<EventSource | null>(null);

  // Read once — stable for the connection lifetime because setActingTeacher always
  // triggers a full page reload, rebuilding this hook with the current value.
  const actingTeacherId = useAuthStore.getState().actingTeacherId;

  useEffect(() => {
    if (!enabled) return;

    function connect() {
      const params = actingTeacherId
        ? `?actAsTeacherId=${actingTeacherId}`
        : "";
      const url = `${BASE_URL}/events/stream${params}`;
      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;

      es.onopen = () => {
        retryDelayRef.current = BACKOFF_BASE_MS;
        // Never let a subscriber's error kill the connection it just opened.
        try {
          optionsRef.current?.onOpen?.();
        } catch (err) {
          console.error("SSE onOpen handler threw:", err);
        }
      };

      es.onerror = () => {
        es.close();
        esRef.current = null;
        const delay = retryDelayRef.current;
        retryDelayRef.current = Math.min(delay * 2, BACKOFF_MAX_MS);
        retryTimerRef.current = setTimeout(connect, delay);
      };

      // Event types are registered once at connect time. Existing handler
      // implementations stay current via handlersRef, but new event type keys
      // added after connect won't be listened to until the next reconnect.
      const registeredTypes = Object.keys(handlersRef.current);
      for (const eventType of registeredTypes) {
        es.addEventListener(eventType, (event: MessageEvent) => {
          try {
            const data = JSON.parse((event as MessageEvent).data);
            handlersRef.current[eventType]?.(data);
          } catch {
            handlersRef.current[eventType]?.(event.data);
          }
        });
      }
    }

    connect();

    return () => {
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      esRef.current?.close();
      esRef.current = null;
      retryDelayRef.current = BACKOFF_BASE_MS;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
